import React, { createContext, useContext, ReactNode } from 'react';
import useGamification, {
    GamificationStatus,
    TrackResult,
    LevelInfo
} from '../hooks/useGamification';
import { XPNotification } from '../components/gamification';

interface GamificationContextValue {
    status: GamificationStatus | null;
    loading: boolean;
    trackAction: (actionCode: string) => Promise<TrackResult | null>;
    refreshStatus: () => Promise<void>;
    isEnabled: boolean;
}

const GamificationContext = createContext<GamificationContextValue | null>(null);

interface GamificationProviderProps {
    children: ReactNode;
}

/**
 * Gamification Provider
 * Wraps the app to provide gamification features
 * Fail-safe: If anything goes wrong, the app continues normally
 */
export const GamificationProvider: React.FC<GamificationProviderProps> = ({ children }) => {
    const gamification = useGamification();

    return (
        <GamificationContext.Provider value={gamification}>
            {children}

            {/* Global XP notification toast */}
            <XPNotification
                reward={gamification.lastReward}
                onClose={gamification.clearLastReward}
            />
        </GamificationContext.Provider>
    );
};

/**
 * Hook to access gamification context
 * Returns null values if provider is not present (fail-safe)
 */
export const useGamificationContext = (): GamificationContextValue => {
    const context = useContext(GamificationContext);

    // Fail-safe: Return no-op values if context is not available
    if (!context) {
        return {
            status: null,
            loading: false,
            trackAction: async () => null,
            refreshStatus: async () => { },
            isEnabled: false,
        };
    }

    return context;
};

export default GamificationProvider;
